import { useLayoutEffect, useId, useMemo, useRef } from "react";
import IranMapSimple from "iran-map-simple";
import { provinces } from "./geography";
import { useObservatoryCopy } from "./copy";
import { countRange } from "./model";
import type { ObservatoryView } from "./schema";

export function IranReportingMap({
  selected,
  views,
  color,
  onSelect,
}: {
  selected?: string | undefined;
  views: ObservatoryView[];
  color: (value: number) => string;
  onSelect: (location: string) => void;
}) {
  const { locale, t } = useObservatoryCopy();
  const scope = useId();
  const ref = useRef<HTMLDivElement>(null);
  const label = (id: string) => {
    const province = provinces[+id];
    if (!province) return "";
    const view = views.find((v) => v.location === `province:${province.name}`);
    return `${locale === "fa" ? province.fa : province.name} · ${view ? countRange(view.cases) : t("unpublishedAreas")}`;
  };
  const signature = useMemo(
    () => JSON.stringify({ locale, selected, views }),
    [locale, selected, views],
  );
  // The upstream SVG owns Persian data-title values. Localize before paint,
  // including the first frame after a date request remounts the map.
  useLayoutEffect(() => {
    ref.current
      ?.querySelectorAll<SVGPathElement>("path[data-id]")
      .forEach((node) => {
        const province = provinces[+node.dataset.id!];
        if (!province) return;
        node.setAttribute("data-location", `province:${province.name}`);
        node.setAttribute(
          "data-area-name",
          locale === "fa" ? province.fa : province.name,
        );
        const text = label(node.dataset.id!);
        node.setAttribute("data-title", text);
        node.setAttribute("aria-label", text);
        node.setAttribute("role", "button");
        node.setAttribute("aria-pressed", String(node.dataset.id === selected));
        node.setAttribute("tabindex", "0");
      });
  }, [signature]);
  function select(id: string) {
    const province = provinces[+id];
    if (province)
      onSelect(id === selected ? "country:364" : `province:${province.name}`);
  }
  return (
    <div
      className="observatory-iran"
      data-reporting-map={scope}
      ref={ref}
      onKeyDown={(event) => {
        const id = (event.target as HTMLElement).getAttribute("data-id");
        if (id && ["Enter", " "].includes(event.key)) {
          event.preventDefault();
          select(id);
        }
      }}
    >
      <style>
        {views
          .map((view) => {
            const province = Object.entries(provinces).find(
              ([, p]) => view.location === `province:${p.name}`,
            );
            return province
              ? `[data-reporting-map="${scope}"] .iran-map-simple__province[data-id="${province[0]}"]:not(.iran-map-simple__province--selected){fill:${color(view.cases.lower)}!important}`
              : "";
          })
          .join("\n")}
      </style>
      <IranMapSimple
        key={`${locale}-${selected ?? "all"}`}
        width={640}
        defaultProvinces={selected ? [+selected] : []}
        fillColor="#b9d0d2"
        hoverColor="#8cbabc"
        selectedColor="#fff0bd"
        onSelect={(ids) => {
          const province = provinces[ids.at(-1) ?? 0];
          onSelect(province ? `province:${province.name}` : "country:364");
        }}
      />
    </div>
  );
}
